import { z } from "zod"

import { startProcessRunSchema, type TrainingsEinheit } from "./schemas"

import { IDD_PRODUKT_TRAININGS, IDD_SZENARIEN } from "./idd-katalog"

/**
 * The IDD area: learning activities that count towards the yearly IDD
 * continuing-education duty (15 hours per calendar year).
 *
 * Kept apart from the free coaching on purpose. Only what is defined in this
 * catalog can be credited, each module with fixed rules, completion criteria
 * and a fixed number of minutes. A free conversation never counts.
 *
 * The answer keys of the knowledge check are NOT here: this package ships to
 * the browser. They live in `apps/api/src/idd/idd-fragen.ts`.
 */

/** Pass mark of the knowledge check at the end of a product training. */
export const IDD_BESTEHENSGRENZE = 0.7

/** Questions drawn per chapter for one attempt of the knowledge check. */
export const IDD_FRAGEN_JE_KAPITEL = 2

export const iddArtSchema = z.enum(["gespraech", "produkt"])
export type IddArt = z.infer<typeof iddArtSchema>

export const IDD_ART_LABELS: Record<IddArt, string> = {
  gespraech: "IDD-Gesprächstraining",
  produkt: "IDD-Produkttraining",
}

export type IddMeilensteinDef = {
  key: string
  titel: string
  /** What counts as reached. Shown to the advisor and given to the judge verbatim. */
  kriterium: string
}

/**
 * A conversation module is one training unit of the Gesprächstraining, run as
 * a regular Durchlauf with exactly that unit. Roleplay, call, reflection and
 * feedback are the existing ones; the IDD layer adds the mandatory steps and
 * the credit rules. `key` equals `einheit`.
 */
export type IddSzenario = {
  key: TrainingsEinheit
  einheit: TrainingsEinheit
  titel: string
  kurz: string
  /** Generic on purpose: it goes into the milestone prompt for any customer. */
  beschreibung: string
  /** The customer of the "Starten" shortcut; "Selbst zusammenstellen" picks any. */
  vorlagePersonaId: string
  /** Fixed IDD time, independent of how long the conversation actually took. */
  minuten: number
  /** What the advisor is asked to achieve, shown beside the conversation. */
  auftrag: string
  meilensteine: IddMeilensteinDef[]
}

export type IddKapitelDef = {
  key: string
  titel: string
  beschreibung: string
  minuten: number
  /** The coach's opening question when the chapter starts. */
  einstieg: string
}

export type IddProduktTraining = {
  key: string
  /** Matches ProduktKey: the product snippets are loaded by it. */
  produkt: string
  titel: string
  beschreibung: string
  mindestKapitel: number
  kapitel: IddKapitelDef[]
}

/**
 * Bumped whenever a scenario or chapter changes in substance. Stored on every
 * training and certificate, so a certificate keeps saying which rules it was
 * earned under.
 */
export const IDD_KATALOG_FASSUNG = "2026-11 Prototyp, Module je Einheit"

export { IDD_PRODUKT_TRAININGS, IDD_SZENARIEN } from "./idd-katalog"

/** Conversation modules that exist for a unit, for the configurator's IDD mode. */
export function iddSzenarioFuerEinheit(einheit: TrainingsEinheit): IddSzenario | undefined {
  return IDD_SZENARIEN.find((s) => s.einheit === einheit)
}

export function iddSzenario(key: string): IddSzenario | undefined {
  return IDD_SZENARIEN.find((s) => s.key === key)
}

export function iddProduktTraining(key: string): IddProduktTraining | undefined {
  return IDD_PRODUKT_TRAININGS.find((p) => p.key === key)
}

/** Credited minutes of a product training: the sum of its chosen chapters. */
export function iddProduktMinuten(
  training: IddProduktTraining,
  kapitel: readonly string[],
): number {
  return training.kapitel
    .filter((k) => kapitel.includes(k.key))
    .reduce((summe, k) => summe + k.minuten, 0)
}

/**
 * The calendar year a moment belongs to, in German time.
 *
 * Not `getFullYear()`: the server runs in UTC, and a module completed at
 * 00:30 on New Year's Day in Hamburg would otherwise be credited to the old
 * year, which is exactly the boundary the yearly reset turns on.
 */
export function iddJahr(datum: Date = new Date()): number {
  const jahr = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
  }).format(datum)
  return Number(jahr)
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const startIddTrainingSchema = z.object({
  art: iddArtSchema,
  modulKey: z.string().min(1),
  /** Only for product trainings: the chosen chapters. */
  kapitel: z.array(z.string().min(1)).max(20).optional(),
  /**
   * Only for conversation modules: the Durchlauf as the configurator would
   * start it. Its units must be exactly the module's unit.
   */
  durchlauf: startProcessRunSchema.optional(),
})
export type StartIddTraining = z.infer<typeof startIddTrainingSchema>

export const iddKapitelAuswahlSchema = z.object({
  kapitel: z.array(z.string().min(1)).max(20),
})
export type IddKapitelAuswahl = z.infer<typeof iddKapitelAuswahlSchema>

export const iddPruefungsAbgabeSchema = z.object({
  antworten: z
    .array(z.object({ frageId: z.string().min(1), gewaehlt: z.string().min(1) }))
    .max(40),
})
export type IddPruefungsAbgabe = z.infer<typeof iddPruefungsAbgabeSchema>

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const iddTrainingStatusSchema = z.enum([
  "laufend",
  "pruefung",
  "abgeschlossen",
  "abgebrochen",
])
export type IddTrainingStatus = z.infer<typeof iddTrainingStatusSchema>

/**
 * How a finished training ended for IDD purposes.
 * `wiederholung`: completed, but the module was already credited this year.
 */
export const iddErgebnisSchema = z.enum([
  "angerechnet",
  "nicht_erfuellt",
  "wiederholung",
])
export type IddErgebnis = z.infer<typeof iddErgebnisSchema>

export const iddMeilensteinStandSchema = z.object({
  key: z.string(),
  titel: z.string(),
  kriterium: z.string(),
  erreicht: z.boolean(),
  /** The advisor's words the judge based it on. */
  beleg: z.string().optional(),
})
export type IddMeilensteinStand = z.infer<typeof iddMeilensteinStandSchema>

export const iddKapitelStandSchema = z.object({
  key: z.string(),
  titel: z.string(),
  minuten: z.number().int(),
  erledigt: z.boolean(),
})
export type IddKapitelStand = z.infer<typeof iddKapitelStandSchema>

/** A question as the advisor sees it: no answer key. */
export const iddPruefungsFrageSchema = z.object({
  id: z.string(),
  kapitel: z.string(),
  frage: z.string(),
  optionen: z.array(z.object({ key: z.string(), text: z.string() })),
})
export type IddPruefungsFrage = z.infer<typeof iddPruefungsFrageSchema>

export const iddPruefungsAuswertungSchema = z.object({
  frageId: z.string(),
  gewaehlt: z.string().optional(),
  richtigeOption: z.string(),
  richtig: z.boolean(),
  begruendung: z.string(),
})

export const iddPruefungsVersuchSchema = z.object({
  id: z.string(),
  fragen: z.array(iddPruefungsFrageSchema),
  /** Present once submitted. */
  quote: z.number().optional(),
  bestanden: z.boolean().optional(),
  auswertung: z.array(iddPruefungsAuswertungSchema).optional(),
  createdAt: z.string(),
  abgegebenAm: z.string().optional(),
})
export type IddPruefungsVersuch = z.infer<typeof iddPruefungsVersuchSchema>

export const iddTrainingSchema = z.object({
  id: z.string(),
  art: iddArtSchema,
  modulKey: z.string(),
  titel: z.string(),
  status: iddTrainingStatusSchema,
  ergebnis: iddErgebnisSchema.optional(),
  /** False when the module was already credited this calendar year. */
  anrechenbar: z.boolean(),
  jahr: z.number().int(),
  minuten: z.number().int(),
  meilensteine: z.array(iddMeilensteinStandSchema),
  kapitel: z.array(iddKapitelStandSchema),
  aktuellesKapitel: z.string().optional(),
  /** Conversation scenarios run as a Durchlauf. */
  processRunId: z.string().optional(),
  /** Product trainings run in a coach thread. */
  threadId: z.string().optional(),
  versuche: z.array(iddPruefungsVersuchSchema),
  nachweisId: z.string().optional(),
  startedAt: z.string(),
  completedAt: z.string().optional(),
})
export type IddTraining = z.infer<typeof iddTrainingSchema>

/** The IDD side of a Durchlauf: empty when the run is no IDD scenario. */
export const iddRunStandSchema = z.object({
  training: iddTrainingSchema.optional(),
  auftrag: z.string().optional(),
})
export type IddRunStand = z.infer<typeof iddRunStandSchema>

export const iddNachweisSchema = z.object({
  id: z.string(),
  nummer: z.string(),
  art: iddArtSchema,
  modulKey: z.string(),
  titel: z.string(),
  inhalte: z.array(z.string()),
  minuten: z.number().int(),
  jahr: z.number().int(),
  abgeschlossenAm: z.string(),
  teilnehmer: z.string(),
  sparkasse: z.string().optional(),
  /** Knowledge-check score of a product training. */
  quote: z.number().optional(),
  katalogFassung: z.string(),
  trainingId: z.string(),
})
export type IddNachweis = z.infer<typeof iddNachweisSchema>
export const iddNachweisListSchema = z.array(iddNachweisSchema)

export const iddTrainingKurzSchema = z.object({
  id: z.string(),
  processRunId: z.string().optional(),
  art: iddArtSchema,
  modulKey: z.string(),
  titel: z.string(),
  status: iddTrainingStatusSchema,
  ergebnis: iddErgebnisSchema.optional(),
  minuten: z.number().int(),
  startedAt: z.string(),
  completedAt: z.string().optional(),
})
export type IddTrainingKurz = z.infer<typeof iddTrainingKurzSchema>

export const iddModulStandSchema = z.object({
  art: iddArtSchema,
  modulKey: z.string(),
  /** Set when credited in the current calendar year. */
  angerechnetAm: z.string().optional(),
  nachweisId: z.string().optional(),
  laufendesTrainingId: z.string().optional(),
  /** The Durchlauf of a running conversation scenario. */
  laufenderDurchlaufId: z.string().optional(),
  /** State of the running training, for its card. */
  laufendStatus: iddTrainingStatusSchema.optional(),
  laufendeKapitel: z.array(iddKapitelStandSchema).optional(),
})
export type IddModulStand = z.infer<typeof iddModulStandSchema>

export const iddUebersichtSchema = z.object({
  jahr: z.number().int(),
  /** The yearly IDD duty, for the progress bar. */
  sollMinuten: z.number().int(),
  minutenImJahr: z.number().int(),
  module: z.array(iddModulStandSchema),
  trainings: z.array(iddTrainingKurzSchema),
  nachweise: z.array(iddNachweisSchema),
})
export type IddUebersicht = z.infer<typeof iddUebersichtSchema>

/** 15 hours a year, § 34d Abs. 9 GewO / § 48 Abs. 2 VAG. */
export const IDD_SOLL_MINUTEN = 15 * 60
